import { createFileRoute, redirect } from "@tanstack/react-router";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { DocumentCheckClient } from "@/features/documentCheck/DocumentCheckClient";
import { PalletCheckClient } from "@/features/pallets/PalletCheckClient";
import type { PalletCheckState } from "@/features/pallets/pallets.api";

// Transport → Palletcontrole (docs/scantafel.md §10): opens on the signed CMRs one by one, for whoever also reads scans.
// Behind it the overview (docs/tms-emballage.md): the consignments with pallets that wait for the check, also without a
// CMR yet, and those checked. Reading takes tms_orders; typing the actual counts and checking take tms_orders_write.
export const Route = createFileRoute("/_shell/pallet-check")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_orders") && !hasModule(context.session, "tms_orders_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): { view?: "overview"; state?: PalletCheckState } => ({
    view: search.view === "overview" ? "overview" : undefined,
    state: (["open", "checked"] as const).find((state) => state === search.state),
  }),
  component: PalletCheckScreen,
});

function PalletCheckScreen() {
  const { view, state } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canWork = hasModule(session, "documents") || hasModule(session, "dossiers") || hasModule(session, "dossiers_write");
  if (canWork && !view && !state) {
    return (
      <div className="wl-page wl-page--list" data-screen-label="Back-office Pallet Check Work">
        <DocumentCheckClient key="pallets" step="pallets" userId={session.userId} canWrite={hasModule(session, "dossiers_write") && hasModule(session, "tms_orders_write")} />
      </div>
    );
  }
  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Pallet Check">
      <PalletCheckClient
        userId={session.userId}
        state={state ?? "open"}
        onState={(next) => navigate({ search: { view: "overview", state: next === "open" ? undefined : next }, replace: true })}
        canWork={canWork}
        canWrite={hasModule(session, "tms_orders_write")}
      />
    </div>
  );
}
